import api from "../../api.service"
import { getPaginatedResponse } from "../utils"

import queryString from "query-string"

/**
 * For query parameters, see:
 * https://docs.api.missionx.ai/group/endpoint-vendorinvoices
 */
export function getOrgVendorInvoices(orgId, queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/v2/orgs/${orgId}/vendorInvoices?${qs}`, { signal })
        .then((res) => getPaginatedResponse(res.data))
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * Cash outflow forecast for the vendor "Dashboard" tab's easy-win charts - outstanding
 * invoice balances bucketed by days until due. Amounts are converted to the org's own
 * currency by default; pass `currency` (an ISO code from the response's own
 * `availableCurrencies`) to see just that currency's invoices, unconverted.
 * @param orgId {String}
 * @param [currency] {String}
 * @return {Promise<{currency: String, availableCurrencies: String[], cashOutflowByBucket: Array}>}
 */
export function getOrgVendorInvoiceStats(orgId, currency, signal) {
    const qs = currency ? `?${queryString.stringify({ currency })}` : ""
    return api
        .get(`/v2/orgs/${orgId}/vendorInvoices/stats${qs}`, { signal })
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * A single invoice by id. Unlike the paginated list above, this one also grants read
 * access to a person named on the invoice's own approval chain who has no invoice
 * permission in the org otherwise (see isVendorInvoiceApprover in mission-x-api) - so
 * it's the load path anything an approver can reach has to use.
 * @param orgId {String}
 * @param vendorInvoiceId {String}
 * @param [queryParams] {Object}
 * @param [queryParams.expand] {String[]} Any of vendor/poNumber/costCenter/client/
 * sourceFile - populated to the same shape the list endpoint returns them in.
 */
export function getOrgVendorInvoice(orgId, vendorInvoiceId, queryParams = {}, signal) {
    const qs = queryString.stringify(queryParams)
    return api
        .get(`/org/${orgId}/vendorInvoice/${vendorInvoiceId}?${qs}`, { signal })
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * Step 1 of the upload flow - parses the file, stores it, and creates the invoice as a
 * "draft" populated with whatever vendor/PO matched (or blank if nothing did). Both this
 * and confirmOrgVendorInvoice below live on the legacy (non-v2) vendorInvoice module,
 * not under /v2/orgs/:orgId/vendorInvoices - only the paginated GET is v2.
 * @param orgId {String}
 * @param file {File}
 * @param [options] {Object}
 * @param [options.onUploadProgress] {Function}
 * @return {Promise<*>} The created draft invoice - `vendor`/`poNumber` are the
 * best-guess match (or null), always to be confirmed/overridden by the reviewer before
 * calling confirmOrgVendorInvoice. Also includes `groundedTotalAmount`/`groundedPoNumber`.
 */
export function ingestOrgVendorInvoice(orgId, file, { onUploadProgress } = {}) {
    const formData = new FormData()
    formData.append("file", file)

    return api
        .post(`/org/${orgId}/vendorInvoice/ingest`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (progressEvent) => {
                if (typeof onUploadProgress === "function") {
                    onUploadProgress(progressEvent.loaded, progressEvent.total)
                }
            },
        })
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseErrorFileUpload(err)
        })
}

/**
 * Step 2 - the reviewer's accepted/corrected vendor, project/mission (optional), PO
 * (optional - not every invoice has one), total, and dates for an existing draft. No
 * file involved - it's already stored from ingestOrgVendorInvoice. Moves the draft to
 * "pending" or "approved". Note: if a PO is set, its own vendor/mission always win over
 * whatever's submitted here - see vendorInvoice.controller.js#confirmVendorInvoice.
 * @param orgId {String}
 * @param vendorInvoiceId {String} The draft's id, from ingestOrgVendorInvoice's response.
 * @param data {Object}
 * @param data.vendorId {String} Required.
 * @param [data.missionId] {String}
 * @param [data.poId] {String}
 * @param data.totalAmount {Number}
 * @param data.invoiceDate {Number} Epoch ms.
 * @param [data.dueDate] {Number} Epoch ms.
 * @param [data.costCenterId] {String}
 * @param [data.discrepancies] {Array} The draft's discrepancies with `approved` set on the
 * ones the reviewer signed off - only that flag is read server-side, and only for
 * discrepancies still present after the confirm-time recompute.
 * @param [data.discrepancyComment] {String}
 * @return {Promise<*>} The updated invoice, including `discrepancies` and `manualChanges`.
 */
export function confirmOrgVendorInvoice(
    orgId,
    vendorInvoiceId,
    { vendorId, missionId, poId, costCenterId, totalAmount, invoiceDate, dueDate, discrepancies, discrepancyComment },
) {
    return api
        .put(`/org/${orgId}/vendorInvoice/${vendorInvoiceId}/confirm`, {
            vendorId,
            missionId,
            poId,
            costCenterId,
            totalAmount,
            invoiceDate,
            dueDate,
            discrepancies,
            discrepancyComment,
        })
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * Discards a draft the reviewer abandoned (cancelled or closed before accepting) - the
 * only place this is currently called from is UploadInvoice.jsx's cancel/start-over path.
 * @param orgId {String}
 * @param vendorInvoiceId {String}
 */
export function deleteOrgVendorInvoice(orgId, vendorInvoiceId) {
    return api.delete(`/org/${orgId}/vendorInvoice/${vendorInvoiceId}`).catch((err) => {
        throw api.getResponseError(err)
    })
}

/**
 * General field-level update for an existing (non-draft-flow) invoice, e.g. from
 * VendorInvoiceDetails.jsx. Which fields are actually writable depends on the invoice's
 * current status server-side - draft/pending allow everything, anything else only
 * allows status/notes/payments/amountPaid. If `poNumber` is included, its own vendor/
 * mission override whatever's also submitted (same rule as confirmOrgVendorInvoice).
 * @param orgId {String}
 * @param vendorInvoiceId {String}
 * @param change {Object} Partial - only send the fields being changed.
 */
export function updateOrgVendorInvoice(orgId, vendorInvoiceId, change) {
    return api.put(`/org/${orgId}/vendorInvoice/${vendorInvoiceId}`, change).then((res) => res.data).catch((err) => {
        throw api.getResponseError(err)
    })
}

/**
 * Vendor invoices awaiting THIS person's approval, across every org they're in - drives
 * the home screen's approvals tile and its review card. Asks the invoices themselves
 * rather than the notification list, so the tile only clears once the work is actually
 * done (a notification's read state says nothing about whether anything was approved).
 * @param [options] {Object}
 * @param [options.countOnly] {Boolean} Skips the list - what the tile polls with.
 * @return {Promise<{count: Number, list: Array}>} Each list entry carries `approvalMode`:
 * "chain" (act via actOnOrgVendorInvoiceApproval) or "direct" (a plain status update via
 * updateOrgVendorInvoice) - see approvalQueue.controller.js in mission-x-api.
 */
export function getMyVendorInvoiceApprovals({ countOnly } = {}, signal) {
    const qs = countOnly ? "?countOnly=true" : ""
    return api
        .get(`/v2/vendorInvoices/awaiting-approval${qs}`, { signal })
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}

/**
 * Records this person's approve/reject decision on their step(s) of a pending invoice's
 * manual approval chain. Authorization is being a listed, not-yet-acted-on approver on
 * THIS invoice - not canManageInvoices/canManageVendors. Only for invoices whose
 * `approvalMode` is "chain"; a "direct" one is approved with updateOrgVendorInvoice.
 * @param orgId {String}
 * @param vendorInvoiceId {String}
 * @param action {"approve"|"reject"}
 */
export function actOnOrgVendorInvoiceApproval(orgId, vendorInvoiceId, action) {
    return api
        .put(`/org/${orgId}/vendorInvoice/${vendorInvoiceId}/approval`, { action })
        .then((res) => res.data)
        .catch((err) => {
            throw api.getResponseError(err)
        })
}
